<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import { FormLabel, FormInput } from "../../../../base-components/Form";
import Button from "../../../../base-components/Button";
import { computed, onBeforeMount, reactive, ref, toRefs } from "vue";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import useVuelidate from "@vuelidate/core";
import { useUsuariosStore } from "../../../../services/usuarios";
import { useUsuarioRolesStore } from "../../../../services/usuarioRoles";
import { useMensajesStore } from "../../../../services/mensajes";
import Alert from "../../../../base-components/Alert/Alert.vue";
import { LogAcciones, Usuario } from "@fixeduy/restaurantestypes";
import { getCurrentTime } from "../../../../utils/fecha";
import { userUserStore } from "../../../../stores/user";
import { useLogAccionesStore } from "../../../../services/logAcciones";

interface Props {
  usuario: Usuario;
}

const props = defineProps<Props>();
const mensajesStore = useMensajesStore();
const usuariosRolesStore = useUsuarioRolesStore();
const usuariosStore = useUsuariosStore();
const userStore = userUserStore();
const logAccionesStore = useLogAccionesStore();
const cargando = ref(false);

onBeforeMount(() => {
  if (usuariosRolesStore.roles.length == 0) {
    usuariosRolesStore.getRolesFromFirestore();
  }
});

const emit = defineEmits<{ (e: "cerrar"): void }>();

const formData = reactive({
  contrasena: ""
});

const rules: any = computed(() => {
  return {
    contrasena: {
      largoMinimo: {
        $validator: (password: string) => {
          return password.length >= 8;
        },
        $message: "La contraseña debe tener al menos 8 caracteres."
      },
      letraMiniscula: {
        $validator: (password: string) => {
          const regexLetraMin = /[a-zA-Z]/;
          if (!regexLetraMin.test(password)) {
            return false;
          } else {
            return true;
          }
        },
        $message: "La contraseña debe tener al menos una letra minúscula."
      },
      letraMayuscula: {
        $validator: (password: string) => {
          const regexLetraMay = /[A-Z]/;
          if (!regexLetraMay.test(password)) {
            return false;
          } else {
            return true;
          }
        },
        $message: "La contraseña debe tener al menos una letra mayúscula."
      },
      numero: {
        $validator: (password: string) => {
          const regexNum = /[0-9]/;
          if (!regexNum.test(password)) {
            return false;
          } else {
            return true;
          }
        },
        $message: "La contraseña debe tener al menos un número."
      }
    }
  };
});

const validate = useVuelidate(rules, toRefs(formData));

const handleEdit = async () => {
  const result = await validate.value.$validate();

  if (result) {
    cargando.value = true;
    const res = await usuariosStore.establecerContrasena(
      props.usuario,
      formData.contrasena
    );
    if (res.exito) {
      const logAcciones: LogAcciones = {
        id: "",
        accion: [
          `Se establece contraseña a usuario ${props.usuario.nombre} (${props.usuario.id}).`
        ],
        modulo: "usuarios",
        fecha: getCurrentTime(),
        usuario: userStore.userLoggedIn?.uid!,
        mail: userStore.userLoggedIn?.email!
      };

      await logAccionesStore.guardarLog(logAcciones);
      mensajesStore.mostrarToast(
        "Usuario actualizado",
        "Contraseña establecida correctamente",
        mensajesStore.mensajeType.exito
      );
      emit("cerrar");
    } else {
      mensajesStore.mostrarModal(
        "Error estableciendo contraseña",
        `Desde el servidor se recibió el mensaje de error: \n${res.errorMsg}`,
        mensajesStore.mensajeType.error
      );
    }
    cargando.value = false;
  }
};
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="lg">
    <Dialog.Panel>
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">
          Establecer contraseña para usuario
        </h2>
      </Dialog.Title>
      <!-------------------- DIALOG DESCRIPTION ------------------------>

      <Dialog.Description>
        <div class="flex">
          <div class="flex-none w-50 pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1 ml-1"> Mail</FormLabel>
          </div>
          <div class="flex-1 ml-2">
            <FormInput
              type="text"
              disabled="true"
              v-model.trim="props.usuario.mail"
            />
          </div>
        </div>
        <div class="mt-5 text-warning">
          <Alert>
            Si el usuario tiene acceso a más de una empresa, al cambiar su
            contraseña y enviarla de forma manual podrías estar brindando acceso
            a otra empresa inconscientemente.
          </Alert>
        </div>
        <div class="flex mt-5">
          <div class="flex-none w-50 pr-5 mt-2 text-right">
            <FormLabel htmlFor="horizontal-form-1 ml-1" class="w-40 sw:w-20">
              Nueva contraseña</FormLabel
            >
          </div>
          <div class="flex-1">
            <FormInput type="text" v-model.trim="formData.contrasena" />
          </div>
        </div>
        <template v-if="validate.contrasena.$error">
          <div
            v-for="(error, index) in validate.contrasena.$errors"
            :key="index"
            class="mt-2 mb-2 text-danger"
          >
            {{ error.$message }}
          </div>
        </template>
      </Dialog.Description>

      <!-------------------- DIALOG FOOTER ------------------------>
      <Dialog.Footer>
        <div v-if="cargando">
          <div
            class="flex flex-col items-rigth justify-end col-span-6 sm:col-span-3 xl:col-span-2"
          >
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
        <div v-else class="flex justify-between">
          <!-------------------- Botón Cerrar ------------------------>
          <Button
            variant="secondary"
            class="mb-2 mr-3"
            @click="() => emit('cerrar')"
          >
            Cerrar
          </Button>
          <!-------------------- Botón Guardar ------------------------>
          <Button variant="primary" class="mb-2 mr-1" @click="handleEdit">
            Establecer contraseña
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
